/**
 * 表格操作列组件
 *
 * 通用的表格操作列，支持主操作按钮 + "更多" 下拉菜单。
 * 内置确认弹窗、onSuccess 自动回调和预设 action builder（详情/编辑/删除/切换状态）。
 */

import type { MenuProps } from 'antd';
import { App, Button, Dropdown, Popconfirm, Space, theme } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type React from 'react';
import { useMemo } from 'react';

/**
 * 操作项定义
 */
export interface ActionItem {
  /** 唯一标识 */
  key: string;
  /** 显示文本 */
  label: string;
  /** 点击回调 */
  onClick?: () => void;
  /** 类型：primary 显示为主按钮，默认为链接按钮 */
  type?: 'primary' | 'link' | 'text' | 'default';
  /** 危险样式 */
  danger?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否隐藏 */
  hidden?: boolean;
  /** 图标 */
  icon?: React.ReactNode;
  /** 需要确认弹窗 */
  confirm?: {
    title: string;
    description?: string;
    /** 确认回调，支持异步。配合 onSuccess 使用时：解析即成功、抛异常即失败 */
    onConfirm: () => void | Promise<void>;
    /** 操作成功后的提示消息（需配合 ActionColumnProps.onSuccess 使用） */
    successMessage?: string;
  };
}

/**
 * ActionColumn 组件属性
 */
export interface ActionColumnProps {
  /** 主操作（直接展示为按钮） */
  actions?: ActionItem[];
  /** 更多操作（收纳到下拉菜单中） */
  moreActions?: ActionItem[];
  /** "更多" 按钮文本，默认 '更多' */
  moreText?: string;
  /** 按钮大小 */
  size?: 'small' | 'middle' | 'large';
  /** 自定义间距 */
  gap?: number;
  /** confirm 操作成功后的回调（用于刷新列表等） */
  onSuccess?: () => void;
  /** 操作失败时的默认提示消息，默认 '操作失败' */
  defaultErrorMessage?: string;
}

// ======================== onSuccess 包装 ========================

/**
 * 包装 confirm.onConfirm，操作成功后自动提示并调用 onSuccess
 */
function wrapActionsWithOnSuccess(
  actions: ActionItem[] | undefined,
  onSuccess: (() => void) | undefined,
  message: MessageInstance,
  defaultErrorMessage: string,
): ActionItem[] | undefined {
  if (!actions || !onSuccess) return actions;

  return actions.map((action) => {
    if (!action.confirm?.onConfirm) return action;

    const originalOnConfirm = action.confirm.onConfirm;
    const successMsg = action.confirm.successMessage;

    return {
      ...action,
      confirm: {
        ...action.confirm,
        onConfirm: async () => {
          try {
            await originalOnConfirm();
            if (successMsg) {
              message.success(successMsg);
            }
            onSuccess();
          } catch (error) {
            const msg =
              error instanceof Error ? error.message : defaultErrorMessage;
            message.error(msg);
          }
        },
      },
    };
  });
}

/**
 * 渲染单个操作按钮
 */
function renderAction(action: ActionItem, size?: 'small' | 'middle' | 'large') {
  if (action.hidden) return null;

  // 正确映射所有支持的按钮类型
  const buttonType = action.type ?? 'link';
  const isCompact = buttonType === 'link' || buttonType === 'text';

  const button = (
    <Button
      key={action.key}
      type={buttonType}
      danger={action.danger}
      disabled={action.disabled}
      icon={action.icon}
      size={size ?? 'small'}
      onClick={action.confirm ? undefined : action.onClick}
      style={isCompact ? { padding: '0 4px' } : undefined}
    >
      {action.label}
    </Button>
  );

  if (action.confirm) {
    return (
      <Popconfirm
        key={action.key}
        title={action.confirm.title}
        description={action.confirm.description}
        onConfirm={action.confirm.onConfirm}
      >
        {button}
      </Popconfirm>
    );
  }

  return button;
}

/**
 * 表格操作列组件
 *
 * @example
 * ```tsx
 * <ActionColumn
 *   actions={[
 *     { key: 'edit', label: '编辑', onClick: () => goEdit(record.id) },
 *     { key: 'view', label: '查看', onClick: () => goDetail(record.id) },
 *   ]}
 *   moreActions={[
 *     {
 *       key: 'delete', label: '删除', danger: true,
 *       confirm: {
 *         title: '确定要删除吗？',
 *         onConfirm: () => handleDelete(record.id),
 *       },
 *     },
 *   ]}
 * />
 * ```
 */
export function ActionColumn({
  actions = [],
  moreActions = [],
  moreText = '更多',
  size = 'small',
  gap = 0,
  onSuccess,
  defaultErrorMessage = '操作失败',
}: ActionColumnProps): React.ReactElement | null {
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const wrappedActions = useMemo(
    () =>
      wrapActionsWithOnSuccess(
        actions,
        onSuccess,
        message,
        defaultErrorMessage,
      ),
    [actions, onSuccess, message, defaultErrorMessage],
  );
  const wrappedMoreActions = useMemo(
    () =>
      wrapActionsWithOnSuccess(
        moreActions,
        onSuccess,
        message,
        defaultErrorMessage,
      ),
    [moreActions, onSuccess, message, defaultErrorMessage],
  );

  const visibleActions = (wrappedActions ?? actions).filter((a) => !a.hidden);
  const visibleMoreActions = (wrappedMoreActions ?? moreActions).filter(
    (a) => !a.hidden,
  );

  const menuItems: MenuProps['items'] = useMemo(() => {
    return visibleMoreActions.map((action) => {
      if (action.confirm) {
        return {
          key: action.key,
          label: (
            <Popconfirm
              title={action.confirm.title}
              description={action.confirm.description}
              onConfirm={action.confirm.onConfirm}
            >
              <div
                style={{
                  color: action.danger ? token.colorError : undefined,
                  padding: '5px 12px',
                  margin: '-5px -12px',
                }}
              >
                {action.icon}
                {action.icon ? ' ' : ''}
                {action.label}
              </div>
            </Popconfirm>
          ),
          disabled: action.disabled,
        };
      }

      return {
        key: action.key,
        label: (
          <div style={{ color: action.danger ? token.colorError : undefined }}>
            {action.icon}
            {action.icon ? ' ' : ''}
            {action.label}
          </div>
        ),
        disabled: action.disabled,
        onClick: action.onClick,
      };
    });
  }, [visibleMoreActions, token.colorError]);

  if (visibleActions.length === 0 && visibleMoreActions.length === 0) {
    return null;
  }

  return (
    <Space size={gap}>
      {visibleActions.map((action) => renderAction(action, size))}
      {visibleMoreActions.length > 0 && (
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button type="link" size={size} style={{ padding: '0 4px' }}>
            {moreText}
          </Button>
        </Dropdown>
      )}
    </Space>
  );
}

// ======================== Action Builders ========================

/**
 * 创建"查看详情"操作
 */
export function createViewAction(
  onClick: () => void,
  options?: Partial<ActionItem>,
): ActionItem {
  return {
    key: 'view',
    label: '查看',
    onClick,
    ...options,
  };
}

/**
 * 创建"编辑"操作
 */
export function createEditAction(
  onClick: () => void,
  options?: Partial<ActionItem>,
): ActionItem {
  return {
    key: 'edit',
    label: '编辑',
    onClick,
    ...options,
  };
}

/**
 * 创建"删除"操作（带确认弹窗）
 */
export function createDeleteAction(
  onConfirm: () => void,
  options?: Partial<ActionItem> & { confirmTitle?: string },
): ActionItem {
  return {
    key: 'delete',
    label: '删除',
    danger: true,
    confirm: {
      title: options?.confirmTitle ?? '确定要删除此记录吗？',
      onConfirm,
    },
    ...options,
  };
}

/**
 * 创建"切换状态"操作
 *
 * @param currentEnabled - 当前是否为启用状态
 * @param onConfirm - 确认切换回调
 * @param options - 覆盖默认属性（可通过 label / confirm.title 自定义文案）
 * @param labels - 自定义文案 (enableLabel, disableLabel, enableConfirm, disableConfirm)
 */
export function createToggleStatusAction(
  currentEnabled: boolean,
  onConfirm: () => void,
  options?: Partial<ActionItem>,
  labels?: {
    enableLabel?: string;
    disableLabel?: string;
    enableConfirm?: string;
    disableConfirm?: string;
  },
): ActionItem {
  const enableLabel = labels?.enableLabel ?? '启用';
  const disableLabel = labels?.disableLabel ?? '禁用';
  const enableConfirm = labels?.enableConfirm ?? '确定要启用吗？';
  const disableConfirm = labels?.disableConfirm ?? '确定要禁用吗？';

  return {
    key: 'toggleStatus',
    label: currentEnabled ? disableLabel : enableLabel,
    danger: currentEnabled,
    confirm: {
      title: currentEnabled ? disableConfirm : enableConfirm,
      onConfirm,
    },
    ...options,
  };
}
